/* eslint-disable no-console */
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import SelectField from '../../../components/ui/SelectField';
import InputField from '../../../components/ui/InputField';
import EventTypeField from './components/EventTypeField';
import PriceAdjustmentReasonField from '../../../components/PriceAdjustmentReasonField';
import { usePriceAdjustmentAudit } from '../../../hooks/usePriceAdjustmentAudit';
import DateRangeField from '../../../components/ui/DateRangeField';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useDispatch } from 'react-redux';
import { PricePreviewArgs } from '../../../api/booking/pricePreviewApiTypes';
import { usePricePreview } from '../../../hooks/usePricePreview';
import {
    AdditionalService,
    clearBookingCreationStates,
    clearCheckOutDate,
    setAddress,
    setApartments,
    setCheckInDate,
    setCheckOutDate,
    setCity,
    setComment,
    setCompanyName,
    setCountry,
    setEmail,
    setEmailNote,
    setEndTime,
    setEventType,
    setFirstName,
    setLastName,
    setNumberOfGuests,
    setPersoCity,
    setPhone,
    setSelectedApartment,
    setSelectedApartmentDetails,
    setStartTime,
    setZipCode,
} from '../../../store/slices/bookingCreationSlice';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import { useGetLocationListQuery } from '../../../api/locations/locationsApiSlice';
import { useFetchApartments } from '../../../flows/fetchApartmentsFlow';
import { useSlotPrefill } from './useSlotPrefill';
import SlotPrefillNote from './components/SlotPrefillNote';
import { useFetchApartmentDetails } from '../../../flows/fetchApartmentDetailsFlow';
import { useCountryOptions } from '../../../hooks/useCountryOptions';
import { useCreateRoomRentalBooking } from '../../../flows/createRoomRentalBookingFlow';
import BookingCustomerClassSelect from './components/BookingCustomerClassSelect';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import SnackbarComponent from '../../common/SnackbarComponent';
import {
    AdditionalService as AdditionalServiceType,
    Apartment,
    CreateRoomRentalBookingPayload,
    PersonalDataValidation,
} from '../../../types/bookingCreation';
import { useNavigate } from 'react-router';
import { paths } from '../../../routes';
import FilteredSelect from '../../common/FilteredSelect';
import { useFetchBooking } from '../../../flows/fetchBookingsFlow';
import CustomButton from '../../../components/CustomButton';
import CustomerMailBlockedNote from '../../../components/booking/CustomerMailBlockedNote';
import { customerMailRefusalOf, savedOutcomeToast } from '../../../utils/customerMailGuard';
import BookingAttributionFields, {
    BookingAttribution,
    EMPTY_BOOKING_ATTRIBUTION,
} from '../../../components/booking/BookingAttributionFields';
import { applyContactPrefill } from '../../../utils/organizationPrefill';
import { attributionPayload } from '../../../utils/costCenter';
import { useAttributedClassId } from '../../../hooks/useAttributedClassId';
import {
    BOOKING_EMAIL_NOTE_MAX_LENGTH,
    extractAddress,
    isCreateBookingValid,
    sortByLocaleName,
} from '../../../utils';
import { useEventTypeOptions } from '../../../hooks/useEventTypeOptions';
import { useGuestCapacityGuard } from '../../../hooks/useGuestCapacityGuard';
import { getAvailableBookingTimes } from '../../../utils/dateTimeUtils';
import { pickedInstantOrNow, stationDateTime } from '../../../utils/stationTime';
import { useLocationTimeZone } from '../../../hooks/useLocationTimeZone';
import VenueReservationSummary from '../../../components/VenueReservationSummary';
import CustomModal from '../../common/CustomModal';
import AdditionalServices from './components/AdditionalServices/AdditionalServices';
import { roomNounOf } from '../../../utils/useCaseLabels';

dayjs.extend(isSameOrBefore);

interface CreateVenueBookingPageProps {
    /**
     * The event-flow use case this screen books: `venue`, or `hall`, which
     * shares the screen (PO, 2026-10-18). Its locations, the room search, the
     * price preview and the created order all carry it.
     */
    useCase?: string;
}

const CreateVenueBookingPage = ({ useCase = 'venue' }: CreateVenueBookingPageProps) => {
    // The room noun of the screen's copy (venue / hall).
    const noun = roomNounOf(useCase);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        options: countryOptions,
        toLabel: toCountryLabel,
        toValue: toCountryValue,
    } = useCountryOptions();
    const navigate = useNavigate();
    const [cities, setCities] = useState<string[]>([]);
    const { data: locations } = useGetLocationListQuery(useCase);
    const [apartmentOptions, setApartmentOptions] = useState<{id: string, name: string}[]>([]);

    const [loadingCities, setLoadingCities] = useState(true);
    const [loadingApartments, setLoadingApartments] = useState(false);
    const [open, setOpen] = useState(false);
    const [discount, setDiscount] = useState<number>(0);
    // #1942: a price adjustment made here is stored on the reservation with the
    // same audit trail an edit writes — reason + acting admin.
    const {
        reason: priceAdjustmentReason,
        setReason: setPriceAdjustmentReason,
        payload: priceAdjustmentAudit,
    } = usePriceAdjustmentAudit(discount);
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });
    const [loadingCreateBookingOnlySave, setLoadingCreateBookingOnlySave] = useState(false);
    const [loadingCreateBookingSaveAndSend, setLoadingCreateBookingSaveAndSend] = useState(false);
    const [bookingFor] = useState('customer');
    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    const useCases = config?.services;

    const createRoomRentalBooking = useCreateRoomRentalBooking();
    // #1808: the per-booking class override. Local rather than in the booking
    // slice because it is an input to pricing, not part of the booking record
    // the slice mirrors — the backend stores what it RESOLVED, which may differ.
    const [customerClassId, setCustomerClassId] = useState<string | null>(null);
    // #1810: the organisation (unit) the booking is made for, and the invoice
    // recipient's cost centre.
    const [attribution, setAttribution] = useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    // Bumped when a save starts the next booking: the field remounts, so it
    // also forgets what it filled and whether a cost centre was typed.
    const [attributionFormKey, setAttributionFormKey] = useState(0);
    const inheritedClassId = useAttributedClassId(attribution);
    const fetchApartmentDetails = useFetchApartmentDetails();
    const fetchBooking = useFetchBooking(useCases ?? ['']);

    const onFirstNameChange = (firstName: string) =>
        dispatch(setFirstName({ firstName }));
    const onLastNameChange = (lastName: string) =>
        dispatch(setLastName({ lastName }));
    const onPhoneChange = (phone: string) => {
        dispatch(setPhone({ phone: phone }));
    };
    const onCompanyNameChange = (company: string) => {
        dispatch(setCompanyName({ company: company }));
    };
    const onCommentChange = (comment: string) => {
        dispatch(setComment({ comment: comment }));
    };
    const onEmailNoteChange = (emailNote: string) => {
        dispatch(setEmailNote({ emailNote: emailNote }));
    };
    const onEmailChange = (email: string) => dispatch(setEmail({ email }));

    const onAddressChange = (address: string) =>
        dispatch(setAddress({ address }));
    const onCityChange = (city: string) => dispatch(setPersoCity({ city }));
    const onCountryChange = (country: string) => {
        dispatch(setCountry({ country: country }));
    };
    const onZipCodeChange = (zipCode: string) => {
        dispatch(setZipCode({ zipCode }));
    };

    const personalDetails = useSelector(
        (state: RootState) => state.bookingCreation.personalData,
    );

    const someoneElseDetails = useSelector(
        (state: RootState) => state.bookingCreation.someoneElseData,
    );

    const city = useSelector(
        (state: RootState) => state.bookingCreation.orderData.city,
    );
    const orderData = useSelector(
        (state: RootState) => state.bookingCreation.orderData,
    );

    const startDate = useSelector(
        (state: RootState) => state.bookingCreation.orderData.checkInDate,
    );
    const endDate = useSelector(
        (state: RootState) => state.bookingCreation.orderData.checkOutDate,
    );
    const numberOfGuests = useSelector(
        (state: RootState) => state.bookingCreation.orderData.numberOfGuests,
    );
    const comment = useSelector(
        (state: RootState) => state.bookingCreation.orderData.comment,
    );
    const emailNote = useSelector(
        (state: RootState) => state.bookingCreation.orderData.emailNote,
    );
    const startTime = useSelector(
        (state: RootState) => state.bookingCreation.orderData.startTime,
    );
    const endTime = useSelector(
        (state: RootState) => state.bookingCreation.orderData.endTime,
    );
    const eventType = useSelector(
        (state: RootState) => state.bookingCreation.orderData.eventType,
    );
    // Configured-else-standard venue vocabulary — see useEventTypeOptions. The
    // current value is passed in so a name typed for a free-text room still
    // shows after switching to a dropdown room, instead of a blank select
    // over a value that would still be submitted.
    const eventTypeOptions = useEventTypeOptions(eventType, useCase);

    const apartments = useSelector(
        (state: RootState) => state.bookingCreation.apartments,
    );

    const selectedApartment = useSelector(
        (state: RootState) => state.bookingCreation.selectedApartment,
    );
    // #3219: the picked days and hours are the clock of the venue's location
    // (the station default until a venue is picked).
    const timeZone = useLocationTimeZone(selectedApartment?.locationId);

    const selectedApartmentDetails = useSelector(
        (state: RootState) => state.bookingCreation.selectedApartmentDetails,
    );

    const personalDataValidation: PersonalDataValidation = useSelector(
        (state: RootState) => state.bookingCreation.personalDataValidation,
    );


    const selectedAdditionalServices: AdditionalService[] = useSelector(
        (state: RootState) => state.bookingCreation.selectedAdditionalServices,
    );

    const confirmEnabled = useSelector(
        (state: RootState) => state.bookingCreation.confirmEnabled,
    );

    const additionalServices: AdditionalServiceType[] =
        selectedApartmentDetails?.assetAdditionalServices ?? [];

    const styles = {
        paper: (theme: any) => ({
            padding: '20px',
        }),
    };

    const handleDateChange = (start: Dayjs | null, end: Dayjs | null) => {
        if (start) {
            dispatch(setCheckInDate({ checkInDate: start.startOf('day') }));
            dispatch(clearCheckOutDate());
        }
        if (end) {
            dispatch(setCheckOutDate({ checkOutDate: end }));
        }
    };

    const generateAvailableDates = (minDate?: Dayjs | null) => {
        const today = new Date();
        const start = minDate || dayjs(today).startOf('day');
        const end = dayjs().add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };

    const generateAvailableCheckOutDates = (checkInDate: Dayjs | null) => {
        if (!checkInDate) return generateAvailableDates();
        const start = checkInDate.isBefore(dayjs().startOf('day')) ? dayjs().startOf('day') : checkInDate;
        const end = dayjs()?.add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date?.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };

    const availableCheckInDates = generateAvailableDates();
    const availableCheckOutDates = generateAvailableCheckOutDates(startDate);


    // Guests may not exceed the selected object's capacity. Enforced by the
    // API (ADR-0020); shown here so the operator sees it while typing.
    const {
        capacity: objectCapacity,
        exceeds: guestsExceedCapacity,
        blocksSave: guestsBlockSave,
    } = useGuestCapacityGuard({
        selectedObject: selectedApartment,
        selectedObjectDetails: selectedApartmentDetails,
        guests: numberOfGuests,
    });

    const saveOnlyButtonDisabled = isCreateBookingValid(
        {
            orderData: orderData,
            apartment: selectedApartment,
            personalData: personalDetails,
            someoneElseData: someoneElseDetails,
        },
        ['firstName', 'lastName'],
    ) && !guestsBlockSave;

    useEffect(() => {
        dispatch(clearBookingCreationStates());
        dispatch(setApartments([]));
    }, []);

    // A slot picked in the operator week calendar (#3039). After the clear
    // above on purpose — effects run in call order.
    const slotPrefillStatus = useSlotPrefill({
        locations: locations,
        apartments,
        loadingApartments,
        selectedApartment,
        numberOfGuests,
    });

    useEffect(() => {
        if (locations && locations.length > 0) {
            setCities(
                Array.from(
                    new Set(locations.map((loc: any) => loc.address.city)),
                ),
            );
            setLoadingCities(false);
        }
    }, [locations]);

    const getDateTime = (date: Dayjs | null, time: string | null | undefined) => {
        if (!date || !time) return null;
        // The picked hour is the station's wall clock (ADR-0019).
        return stationDateTime(date, time, timeZone);
    };

    const combinedStartDateTime = getDateTime(startDate, startTime ?? null);
    const combinedEndDateTime = getDateTime(endDate, endTime ?? null);

    const fetchApartments = useFetchApartments(
        combinedStartDateTime,
        combinedEndDateTime,
        city,
        numberOfGuests,
        undefined, // orderId
        useCase,
    );

    useEffect(() => {
        const getApartments = async () => {
            if (city && combinedStartDateTime && combinedEndDateTime && numberOfGuests && fetchApartments) {
                setLoadingApartments(true);
                await fetchApartments({ includeCurrentApartment: true });
                setLoadingApartments(false);
            }
        };
        getApartments();
    }, [city, combinedStartDateTime, combinedEndDateTime, numberOfGuests, fetchApartments]);

    const currentLanguage = i18n.language;

    useEffect(() => {
        if (apartments) {
            setApartmentOptions(sortByLocaleName(apartments.map((apart: Apartment) => {
                const displayName = apart?.langNames?.[currentLanguage]?.title || apart?.name;
                return {
                    id: apart.id,
                    name: displayName,
                };
            }), (option) => option.name));
        }
    }, [apartments, currentLanguage]);

    useEffect(() => {
        const getApartmentDetails = async () => {
            dispatch(setSelectedApartmentDetails(null));
            if (selectedApartment) {
                await fetchApartmentDetails();
            }
        };
        getApartmentDetails();
    }, [selectedApartment]);

    const fetchOrders = async () => {
        await fetchBooking();
    };

    let availableStartTimes: string[] = [];
    let availableEndTimes: string[] = [];
    if (startDate && endDate) {
        const { availableStartTimes: startTimes, availableEndTimes: endTimes } = getAvailableBookingTimes(startTime, startDate, endDate, timeZone);
        availableStartTimes = startTimes;
        availableEndTimes = endTimes;
    }

    const handleStartTimeChange = (value: string) => {
        dispatch(setStartTime({ startTime: value }));
    };

    const handleEndTimeChange = (value: string) => {
        dispatch(setEndTime({ endTime: value }));
    };


    const handleCreateBooking = (saveMode: 'save_only' | 'save_and_send_email') => {
        const createBooking = async (saveMode: 'save_only' | 'save_and_send_email') => {
            if (saveMode === 'save_only') {
                setLoadingCreateBookingOnlySave(true);
            } else {
                setLoadingCreateBookingSaveAndSend(true);
            }

            // The picked day at the picked time on the station's clock; a day
            // without a time is station midnight, not the browser's.
            const checkInWithTime = pickedInstantOrNow(startDate, startTime, timeZone);
            const checkOutWithTime = pickedInstantOrNow(endDate, endTime, timeZone);

            const createRoomRentalBookingPayload: CreateRoomRentalBookingPayload = {
                checkIn: checkInWithTime,
                checkOut: checkOutWithTime,
                startTime: startTime,
                endTime: endTime,
                city: city ?? '',
                numberOfGuests: numberOfGuests,
                apartmentId: selectedApartment?.assetId ?? '',
                // #2650: name the offer, not just the object. A divided room's
                // objects all belong to a segment, so without this the whole
                // room silently books one of its parts.
                groupId: selectedApartment?.groupId ?? '',
                // #1808: null means "no override" — the backend then resolves
                // account -> organization -> the tenant's system default.
                customerClassId: customerClassId,
                ...attributionPayload(attribution),
                locationId: selectedApartment?.locationId ?? '',
                additionalServices: selectedAdditionalServices,
                customerDetails: {
                    firstName: personalDetails.firstName,
                    lastName: personalDetails.lastName,
                    email: personalDetails.email,
                    phone: personalDetails.phone,
                    address: personalDetails.address,
                    country: personalDetails.country,
                    city: personalDetails.city,
                    zipCode: personalDetails.zipCode,
                    company: personalDetails.company,
                },
                someoneElseDetails:
                        bookingFor === 'customer'
                            ? {}
                            : {
                                firstName: someoneElseDetails.firstName,
                                lastName: someoneElseDetails.lastName,
                                email: someoneElseDetails.email,
                                phone: someoneElseDetails.phone,
                            },
                useCase,
                discount: discount,
                // #1942: an adjustment is persisted with its reason and the
                // admin who applied it, so it is as traceable as one made on
                // the edit screen. Empty without an adjustment.
                ...priceAdjustmentAudit,
                comment: comment,
                emailNote: emailNote,
                eventType: eventType,
            };

            // return console.log('createRoomRentalBookingPayload', createRoomRentalBookingPayload);

            const { success, mailBlocked } = await createRoomRentalBooking(
                createRoomRentalBookingPayload,
                saveMode,
            );
            if (success) {
                setSnackbarData({
                    open: true,
                    // Saved — but a refused mail is said, never shown as a plain success.
                    ...savedOutcomeToast(t, mailBlocked, t('create_booking.response.success')),
                });
                setTimeout(() => {
                    fetchOrders();
                    navigate(paths.rentalBooking);
                }, 1500);
                dispatch(clearBookingCreationStates());
                // The customer fields just cleared live in the store; the
                // organisation, unit and cost centre are this page's own state.
                setAttribution(EMPTY_BOOKING_ATTRIBUTION);
                setAttributionFormKey((k) => k + 1);
                if (saveMode === 'save_only') {
                    setLoadingCreateBookingOnlySave(false);
                } else {
                    setLoadingCreateBookingSaveAndSend(false);
                }
            } else {
                setSnackbarData({
                    open: true,
                    message: t('create_booking.response.failure'),
                    severity: 'error',
                });
                if (saveMode === 'save_only') {
                    setLoadingCreateBookingOnlySave(false);
                } else {
                    setLoadingCreateBookingSaveAndSend(false);
                }
            }
        };
        createBooking(saveMode);
    };

    const handleCloseSnackbar = () => {
        setSnackbarData((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        if (!loadingCities && cities?.length === 1 && !city) {
            dispatch(setCity({ city: cities[0] }));
        }
    }, [cities, city, loadingCities, dispatch]);

    // Booking price single source of truth (#1203): the price box renders the
    // backend breakdown from POST /admin/order/price-preview. No FE total math.
    const previewArgs: PricePreviewArgs | undefined = useMemo(() => {
        const assetId = selectedApartment?.assetId;
        if (!assetId || !startDate || !endDate) return undefined;
        // #1713: with both times chosen, preview the SAME window the booking
        // is created with (see the submit handler) — hourly/half-day units
        // and per-hour services price by it, and a date-only window would
        // preview a different number than the charge. The backend classifies
        // days in the location's zone (`localCalendarDay`, venueRate.ts), so a
        // location-time instant lands on the right day. Without times: calendar
        // dates, which the resolver reads as whole days.
        const withTime = (date: Dayjs, time: string) => stationDateTime(date, time, timeZone).toISOString();
        const timed = !!startTime && !!endTime;
        return {
            useCase,
            assetId,
            checkIn: timed ? withTime(startDate, startTime) : startDate.format('YYYY-MM-DD'),
            checkOut: timed ? withTime(endDate, endTime) : endDate.format('YYYY-MM-DD'),
            services: selectedAdditionalServices.map((s) => ({
                serviceId: s.serviceId,
                quantity: s.quantity ?? 1,
            })),
            discount,
            amountPaid: 0,
            // #2980 D2: the class this booking will be priced by.
            customerClassId,
            // #1810: the attributed organisation (unit) is part of the class
            // chain the create prices by.
            organizationId: attribution.organizationId,
            organizationUnitId: attribution.organizationUnitId,
        };
    }, [
        selectedApartment?.assetId,
        startDate,
        endDate,
        startTime,
        endTime,
        selectedAdditionalServices,
        discount,
        customerClassId,
        attribution.organizationId,
        attribution.organizationUnitId,
        timeZone,
        useCase,
    ]);

    const { breakdown, previewStatus } = usePricePreview(previewArgs);
    // PO, 2026-10-30: the backend says whether this booking's customer mail
    // may go out; "save" stays available either way.
    const mailRefusal = customerMailRefusalOf(breakdown?.customerMail);

    // Guard the save button against a negative billed total (BE-derived).
    const isTotalNegative = (breakdown?.total ?? 0) < 0;

    return (
        <Container maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Typography variant={'h5'}>
                        {t('create_booking.title')}
                    </Typography>
                </Stack>
                <Paper sx={styles.paper}>
                    <Typography variant={'h3'} className={'mb-3'}>
                        {t('create_booking.reservation_details.title')}
                    </Typography>
                    {/* No `spacing` on this outer container — its children are Grid containers,
                        not items, so only MUI's negative row margin would land and pull the first
                        field into the heading. Same shape on the three sibling booking screens;
                        see docs/design-decisions.md, "Booking form section spacing". */}
                    <Grid
                        container
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            display: 'flex',
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            className={'w-[100%] xl:w-[30%] mt-1 xl:mt-0 mb-8 xl:mb-0 flex justify-end items-end order-1 xl:order-2'}
                        >
                            <VenueReservationSummary
                                isCreateBooking={true}
                                isEditBooking={false}
                                breakdown={breakdown}
                                previewStatus={previewStatus}
                                couponApplied={discount}
                                onDiscountChange={(newDiscount) => setDiscount(newDiscount)}
                                amountPaid={0}
                                numberOfGuests={numberOfGuests}
                                startDate={startDate?.toISOString()}
                                endDate={endDate?.toISOString()}
                            />
                            <PriceAdjustmentReasonField
                                discount={discount}
                                value={priceAdjustmentReason}
                                onChange={setPriceAdjustmentReason}
                            />
                        </Grid>
                        <Grid
                            container
                            spacing={2}
                            className={'w-[100%] xl:w-[70%] flex justify-start items-start order-2 xl:order-1'}
                        >
                            <Grid item xs={12} sm={6}>
                                <DateRangeField
                                    from={startDate ? dayjs(startDate) : null}
                                    to={endDate ? dayjs(endDate) : null}
                                    onChange={(from, to) => handleDateChange(from, to)}
                                    disablePast
                                    allowedStartDays={availableCheckInDates}
                                    allowedEndDays={availableCheckOutDates}
                                    maxDate={dayjs().add(18, 'month').endOf('month')}
                                    label={
                                        t(
                                            'create_booking.reservation_details.booking_period',
                                        ) + ' *'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={availableStartTimes}
                                    value={startTime ?? ''}
                                    onChange={handleStartTimeChange}
                                    label={t('create_booking.reservation_details.start_time')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={availableEndTimes}
                                    value={endTime ?? ''}
                                    onChange={handleEndTimeChange}
                                    label={t('create_booking.reservation_details.end_time')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={cities ?? ['']}
                                    value={city ?? ''}
                                    onChange={(value) =>
                                        dispatch(setCity({ city: value }))
                                    }
                                    disabled={cities.length === 1}
                                    loading={loadingCities}
                                    label={
                                        t(
                                            'create_booking.reservation_details.city',
                                        ) + ' *'
                                    }
                                    noOptions={t(
                                        `create_booking.reservation_details.no_${noun}s`,
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    type={'number'}
                                    min={1}
                                    integerOnly
                                    onChange={(value) =>
                                        dispatch(
                                            setNumberOfGuests({
                                                numberOfGuests: Number(value),
                                            }),
                                        )
                                    }
                                    value={String(numberOfGuests ?? '')}
                                    error={guestsExceedCapacity}
                                    errorText={t(
                                        'create_booking.reservation_details.guests_exceed_object_capacity',
                                        { capacity: objectCapacity ?? 0 },
                                    )}
                                    label={
                                        t(
                                            'create_booking.reservation_details.number_of_guests',
                                        ) + ' *'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={apartmentOptions.map(option => option.name) ?? []}
                                    value={selectedApartment?.name ?? ''}
                                    onChange={(selectedName) => {
                                        if (apartments) {
                                            const selectedOption = apartmentOptions.find(option => option.name === selectedName);
                                            if (selectedOption) {
                                                const selected = apartments.find(
                                                    (apartment) => apartment.id === selectedOption.id,
                                                );
                                                if (selected) {
                                                    dispatch(
                                                        setSelectedApartment(selected),
                                                    );
                                                }
                                            }
                                        }
                                    }}
                                    loading={loadingApartments}
                                    label={
                                        t(
                                            `create_booking.reservation_details.${noun}`,
                                        ) + ' *'
                                    }
                                    noOptions={t(
                                        `create_booking.reservation_details.no_${noun}s_match`,
                                    )}
                                />
                                {
                                    selectedApartment &&
                                    <Typography variant={'body1'} fontWeight={'bold'} sx={{ mt: 2 }}>
                                        {/* eslint-disable-next-line max-len */}
                                        {`${extractAddress(selectedApartment?.name)}, ${selectedApartment?.street}, ${selectedApartment?.postalCode} ${selectedApartment?.city}`}
                                    </Typography>
                                }
                                <SlotPrefillNote status={slotPrefillStatus} />
                                {!apartments && (
                                    <p className={'text-sm text-gray-500 mt-1'}>
                                        {t(
                                            `create_booking.reservation_details.${noun}_note`,
                                        )}
                                    </p>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <EventTypeField
                                    groupId={selectedApartment?.groupId}
                                    options={eventTypeOptions}
                                    value={eventType}
                                    onChange={(eventType) => dispatch(setEventType({mode: 'create', eventType}))}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {/* Renders nothing when the tenant has only
                                    the seeded default class — see the
                                    component for why this one hides where the
                                    organization field shows a hint. */}
                                <BookingCustomerClassSelect
                                    value={customerClassId}
                                    inheritedClassId={inheritedClassId}
                                    onChange={setCustomerClassId}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-2'}>
                        {t('create_booking.additional_services.title')}
                    </Typography>
                    {selectedApartment ? (
                        selectedApartmentDetails ? (
                            <AdditionalServices
                                additionalServices={additionalServices}
                            />
                        ) : (
                            <div
                                className={
                                    'flex items-center space-x-2 text-gray-500'
                                }
                            >
                                <CircularProgress
                                    className={'text-gray-500'}
                                    size={20}
                                />
                                <Typography className={'text-gray-500'}>
                                    {t('common.loading')}
                                </Typography>
                            </div>
                        )
                    ) : (
                        <Typography>
                            {t(`create_booking.additional_services.note_${noun}`)}
                        </Typography>
                    )}
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-3'}>
                        {t('create_booking.customer_details.title')}
                    </Typography>
                    <Grid
                        container={true}
                        spacing={2}
                        className={'w-[100%] xl:w-[70%]'}
                        sx={{ justifyContent: 'flex-start' }}
                    >
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onFirstNameChange}
                                value={personalDetails.firstName}
                                label={
                                    t(
                                        'create_booking.customer_details.first_name',
                                    ) + ' *'
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onLastNameChange}
                                value={personalDetails.lastName}
                                label={
                                    t(
                                        'create_booking.customer_details.last_name',
                                    ) + ' *'
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onEmailChange}
                                value={personalDetails.email}
                                label={
                                    t('create_booking.customer_details.email')
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <PhoneNumberComponent
                                initialValue={personalDetails.phone}
                                onChange={onPhoneChange}
                                valid={personalDataValidation.phone}
                                required={false}
                                label={
                                    t('create_booking.customer_details.phone')
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onCompanyNameChange}
                                value={personalDetails.company}
                                label={
                                    t(
                                        'create_booking.customer_details.company',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <BookingAttributionFields
                                key={attributionFormKey}
                                value={attribution}
                                onChange={setAttribution}
                                onOrganizationPicked={(prefill) => applyContactPrefill(prefill, {
                                    company: onCompanyNameChange,
                                    address: onAddressChange,
                                    zipCode: onZipCodeChange,
                                    city: onCityChange,
                                    country: onCountryChange,
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onAddressChange}
                                value={personalDetails.address}
                                label={
                                    t(
                                        'create_booking.customer_details.address',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FilteredSelect
                                options={countryOptions}
                                selectedOption={toCountryLabel(personalDetails.country ?? '')}
                                handleOptionChange={(label) => onCountryChange(toCountryValue(label))}
                                label={
                                    t(
                                        'create_booking.customer_details.country',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onCityChange}
                                value={personalDetails.city}
                                label={
                                    t('create_booking.customer_details.city')
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onZipCodeChange}
                                value={personalDetails.zipCode}
                                label={
                                    t(
                                        'create_booking.customer_details.zip_code',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onCommentChange}
                                value={comment}
                                label={
                                    t(
                                        'create_booking.customer_details.comment',
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onEmailNoteChange}
                                value={emailNote}
                                maxLength={BOOKING_EMAIL_NOTE_MAX_LENGTH}
                                label={
                                    t(
                                        'create_booking.customer_details.email_note',
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />

                    <div className={'flex space-x-2 mt-6'}>
                        <CustomButton
                            title={t(
                                'create_booking.button.cancel',
                            )}
                            onClick={() => navigate(paths.rentalBooking)}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t(
                                'create_booking.button.confirm_only',
                            )}
                            onClick={() => handleCreateBooking('save_only')}
                            loading={loadingCreateBookingOnlySave}
                            disabled={loadingCreateBookingSaveAndSend || loadingCreateBookingOnlySave || !saveOnlyButtonDisabled || isTotalNegative}
                            // disabled={loadingCreateBookingSaveAndSend || loadingCreateBookingOnlySave || !confirmEnabled || isTotalNegative}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t(
                                'create_booking.button.confirm_and_send',
                            )}
                            onClick={() => setOpen(true)}
                            isFilled={true}
                            // loading={loadingCreateBookingSaveAndSend}
                            disabled={
                                loadingCreateBookingSaveAndSend 
                                || loadingCreateBookingOnlySave 
                                || !confirmEnabled 
                                || isTotalNegative
                                || !personalDataValidation.email
                                || !!mailRefusal
                            }
                        />
                    </div>
                    <CustomerMailBlockedNote refusal={mailRefusal} />
                    <SnackbarComponent
                        open={snackbarData.open}
                        message={snackbarData.message}
                        severity={snackbarData.severity}
                        onClose={handleCloseSnackbar}
                    />
                    <CustomModal
                        open={open}
                        onClose={() => setOpen(false)}
                        title={t('create_booking.modal.title')}
                        saveButton={
                            <CustomButton
                                title={t('create_booking.modal.confirm_button')}
                                onClick={() => handleCreateBooking('save_and_send_email')}
                                disabled={loadingCreateBookingSaveAndSend || !personalDataValidation.email || !!mailRefusal}
                                loading={loadingCreateBookingSaveAndSend}
                                isFilled={true}
                                fullWidth={false}
                            />
                        }
                        closeButton={
                            <CustomButton
                                title={t(
                                    'create_booking.modal.cancel_button',
                                )}
                                onClick={() => setOpen(false)}
                                isFilled={false}
                            />
                        }
                        description={t('create_booking.modal.description')}
                    >
                    </CustomModal>
                </Paper>
            </Box>
        </Container>
    );
};

export default CreateVenueBookingPage;
